import React, { useLayoutEffect, useState } from "react";
import ContentstackAppSDK from "@contentstack/app-sdk";
import Extension from "@contentstack/app-sdk/dist/src/uiLocation";
import { isNull } from "lodash";
import { AppFailed } from "../../shared-components/AppFailed/AppFailed";
import { MarketplaceAppContext } from "../contexts/marketplaceContext";
import LoadingScreen from "../../shared-components/LoadingScreen/LoadingScreen";
import { AppRegion, KeyValueObj } from "../../shared-components/common/types/types";
import { DEFAULT_APP_CONFIG } from "../../shared-components/constant";

// const MARKETPLACE_APP_NAME: string = process.env.REACT_APP_MARKETPLACE_APP_NAME as string;

type ProviderProps = {
  children?: React.ReactNode;
};

/**
 * Marketplace App Provider
 * @param children: React.ReactNode
 */
export const MarketplaceAppProvider: React.FC<ProviderProps> = ({ children }) => {
  const [failed, setFailed] = useState<boolean>(false);
  const [appSdk, setAppSdk] = useState<Extension | null>(null);
  const [appConfig, setConfig] = useState<KeyValueObj>(DEFAULT_APP_CONFIG);
  const [appRegion, setAppRegion] = useState<AppRegion>();

  //Extendend app-sdk: This might be added in app-sdk if required in future
  const disableInherentStyle = async (id: string) => {
    const observer = new MutationObserver(
      async () => {
        const app = document.getElementById(id);
        if (app) {
          const value = app.getBoundingClientRect();
          const { width, height, left, right } = value;
          await window.postRobot.sendToParent("disableInherentStyle", { width, height, left, right });
        }
      }
    );

    const mutationObserverConfig = {
      attributes: true,
      childList: true,
      subtree: true,
    };

    observer.observe(window.document.body, mutationObserverConfig);
  }

  // Initialize the SDK and track analytics event
  useLayoutEffect(() => {
    ContentstackAppSDK.init()
      .then(async (appSdk) => {
        await disableInherentStyle("ai-assistant-dropdown");
        setAppSdk(appSdk);
        const appConfig = await appSdk.getConfig();
        if (appConfig && ((appConfig.config && Object.keys(appConfig.config).length) || appConfig.isBrandKitEnabled)) {
          setConfig(appConfig)
        }
        const region = await appSdk.getCurrentRegion()
        setAppRegion(region);
      })
      .catch(() => {
        setFailed(true);
      });
  }, []);

  // wait until the SDK is initialized. This will ensure the values are set
  // correctly for appSdk.
  if (!failed && isNull(appSdk)) {
    return <LoadingScreen showMessage={false} />;
  }

  if (failed) {
    return <AppFailed />;
  }

  return <MarketplaceAppContext.Provider value={{ appSdk, appConfig, appRegion }}>{children}</MarketplaceAppContext.Provider>;
};
